import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { MIN_PASSWORD_LENGTH, STRONG_PASSWORD_PATTERN } from '../constants';
import { updateConfirmPassword, updatePassword } from './state/passwords_form_actions_creator';
import { initialPasswordFormState, PasswordFormState } from './state/passwords_form_state';
import { IAppState } from '../../../app_state';

@Component({
  selector: 'yi-passwords-form',
  template: require('./passwords_form.component.html'),
  styles: [require('./passwords_form.component.scss')]
})

export class PasswordsFormComponent implements OnInit, AfterViewInit {
  @Output() onSubmit: EventEmitter<any> = new EventEmitter();
  @Input() saveBtnText: string;

  loading = false;
  private state: PasswordFormState = initialPasswordFormState;
  changePasswordForm: FormGroup;
  private passwordTooShort;
  private passwordsDoNotMatch;
  weakPassword;
  @ViewChild('invalidPasswordPop') invalidPasswordPop: PopoverDirective;
  @ViewChild('invalidConfirmPasswordPop') invalidConfirmPasswordPop: PopoverDirective;

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<IAppState>,
    private translateService: TranslateService
  ) {
    this.translateService.stream('SIGNUP.CHANGE_PASSWORD.VALIDATION_ERRORS').subscribe(
      (translated) => {
        this.passwordsDoNotMatch = translated['PASSWORD_NOT_MATCH'];
        this.passwordTooShort = translated['MIN_PASS_LENGTH'];
        this.weakPassword = translated['WEAK_PASSWORD'];
      });
  }

  ngOnInit() {
    this.changePasswordForm = this.formBuilder.group({
      password: ['',
        Validators.compose([
          Validators.required,
          Validators.minLength(MIN_PASSWORD_LENGTH),
          Validators.pattern(STRONG_PASSWORD_PATTERN)
        ])
      ],
      confirmPassword: ['',
        Validators.compose([
            Validators.required,
            Validators.minLength(MIN_PASSWORD_LENGTH),
            Validators.pattern(STRONG_PASSWORD_PATTERN),
            this.duplicatePassword
          ]
        )]
    });
  }

  ngAfterViewInit(): void {
    this.store.select(state => state.app.signup.changePassword).subscribe((data) => {
      this.state = data;
      if (this.state.confirmPassword && this.state.confirmPassword.error.show) {
        this.invalidConfirmPasswordPop.show();
        this.loading = false;
      } else {
        this.invalidConfirmPasswordPop.hide();
      }

      if (this.state.password.error.show) {
        this.invalidPasswordPop.show();
        this.loading = false;
      } else {
        this.invalidPasswordPop.hide();
      }

      if (this.state.error) {
        this.loading = false;
      }
    });
  }

  onValueChanged() {
    if (!this.changePasswordForm.controls.password.valid) {
      this.onPasswordError();
    } else {
      const payload = {
        value: this.changePasswordForm.controls.password.value,
        error: {
          show: false,
          message: ''
        }
      };
      this.store.dispatch(updatePassword({password: payload}));
      this.checkPasswordConfirmationError();
    }
  }

  submit(): void {
    if (this.changePasswordForm.controls.password.valid &&
      this.changePasswordForm.controls.confirmPassword.valid &&
      this.passwordsMatch()) {
      this.updateUserPassword();
    }
  }

  hidePopovers() {
    this.invalidConfirmPasswordPop.hide();
    this.invalidPasswordPop.hide();
  }

  private updateUserPassword(): void {
    this.loading = true;
    this.onSubmit.emit([this.state.password.value, this.state.confirmPassword.value]);
  }

  passwordsMatch() {
    return this.state.password.value === this.state.confirmPassword.value ? true : false;
  }

  duplicatePassword(input) {
    if (!input.root || !input.root.controls) {
      return null;
    }

    let exactMatch = input.root.controls.password.value === input.value;
    return exactMatch ? null : { mismatchedPassword: true };
  }

  private checkPasswordConfirmationError() {
    if (this.state.password.value !== this.changePasswordForm.controls.confirmPassword.value) {
      const payload = {
        value: this.changePasswordForm.controls.confirmPassword.value,
        error: {
          show: true,
          message: this.passwordsDoNotMatch
        }
      };
      this.store.dispatch(updateConfirmPassword({confirmPassword: payload}));
    } else {
      const payload = {
        value: this.changePasswordForm.controls.confirmPassword.value,
        error: {
          show: false,
          message: ''
        }
      };
      this.store.dispatch(updateConfirmPassword({confirmPassword: payload}));
    }
  }

  private onPasswordError() {
    if (this.changePasswordForm.controls.password.errors.minlength) {
      const payload = {
        value: this.changePasswordForm.controls.password.value,
        error: {
          show: true,
          message: this.passwordTooShort
        }
      };
      this.store.dispatch(updatePassword({password: payload}));
    } else if (this.changePasswordForm.controls.password.errors.pattern) {
      const payload = {
        value: this.changePasswordForm.controls.password.value,
        error: {
          show: true,
          message: this.passwordTooShort
        }
      };
      this.store.dispatch(updatePassword({password: payload}));
    }
  }
}
